<template>
	<div v-if="uploadFiles.length > 0" class="upload-widget bg-white">
		<FormButton size="lg" customClasses="flex justify-between items-center px-3 !py-2 border-0 w-full bg-surface-9 rounded-t-md"
			@click="setupOpen = !setupOpen">
			<span class="text-s-s text-dark-1">Uploading {{ uploadingCount }} {{ uploadingCount === 1 ? 'file' : 'files' }}</span>
			<icon-base class="ml-auto mr-0 text-dark-7 opacity-50" :width="12" :height="7" :width-view-box="12" :height-view-box="7"><icon-chevron-down-compact /></icon-base>
		</FormButton>
		<div v-show="setupOpen" class="p-3 max-h-[320px] overflow-y-auto" id="setup">
			<div v-for="media in uploadFiles" :key="media.name" class="video-item">
				<div class="flex-nowrap flex flex-wrap gap-0" v-if="!media.mediaInfo">
					<div class="w-full">
						<span :title="media.fileNameOriginal"
							class="video-item__name text-dark-1 text-s-s flex items-center">
							{{ media.fileName }}
						</span>
					</div>
					<div class="w-full" cols>
						<div class="text-center">
							<i class="fas fa-spinner fa-spin" />
						</div>
					</div>
				</div>
				<div class="list-none mb-0" v-else>
					<div class="flex-nowrap flex flex-wrap items-center" no-gutters>
						<div class="w-full" v-if="isMediaCompatible(media.mediaInfo)">
							<span :title="media.fileNameOriginal"
								class="video-item__name text-dark-1 text-500 text-s-s inline-flex items-center">
								{{ media.fileName }}
							</span>
						</div>
						<div class="!grow basis-full w-full" v-else>
							<span :title="media.fileNameOriginal"
								class="video-item__name text-dark-1 text-500 text-s-s inline-flex items-center">{{ media.fileName
								}}</span>
							<span class="color-danger text-s-xs flex">
							<icon-base class="mr-1" :width="16" :height="15" :width-view-box="16" :height-view-box="15"><icon-alert-triangle /></icon-base>
								<span>{{ getErrorMessage(media.mediaInfo) }}</span>
							</span>
						</div>
						<div class="text-right !shrink w-full">
							<div v-show="!media.uploading" class="h-full vod-upload-action justify-end items-center">

								<!-- upload button -->
								<FormButton title="Click to upload this file" type="light" :isIcon="true" size="sm" class="px-1 ml-auto"
									@click="uploadVideoFile(media)" :disabled="media.durationExceeded">
								<span class="flex items-center" v-if="!media.statusProcessing">
									<icon-base class="text-dark-6" :width="16" :height="15" :width-view-box="16" :height-view-box="15"><icon-cloud-upload /></icon-base>
								</span>
									<i v-else class="fa fa-spinner fa-spin" />
								</FormButton>

								<!-- remove button -->
								<FormButton :key="media.fileName" class="px-1 -mr-2" v-show="!media.statusProcessing" type="light" :isIcon="true"
									size="sm" @click="removeVideoFile(media)">
									<icon-base :width="12" :height="12" :width-view-box="12" :height-view-box="12"><icon-close-thin /></icon-base>
								</FormButton>
							</div>
							<div class="inline-flex items-center" v-show="media.uploading">
								<span class="text-s-xs text-dark-6">{{ media.uploadProgress.toFixed(0) }}%</span>
								<FormButton v-if="media.uploadProgress.toFixed(0) < 100" type="light" :isIcon="true" class="px-1 -mr-2" size="sm"
									@click="cancelVideoUpload(media)">
									<icon-base :width="12" :height="12" :width-view-box="12" :height-view-box="12"><icon-close-thin /></icon-base>
								</FormButton>
								<icon-base v-else-if="media.uploadProgress.toFixed(0) == 100" class="ml-1 text-[#65C374]" :width="13" :height="10"><icon-checkmark /></icon-base>
							</div>
						</div>
					</div>
					<div class="flex flex-wrap">
						<div class="w-full">
							<div v-show="media.uploading" class="h-full vod-upload-action justify-end items-center">
								<FormProgress :value="media.uploadProgress" :max="100" variant="main"
									class="progressbar-wrapper w-full" />
							</div>
						</div>
					</div>
					<div class="flex flex-wrap" v-if="media.durationExceeded">
						<div class="text-s-xs text-danger w-full">Your video exceed 10 mins for trial, please upload shorter
							video.</div>
					</div>
				</div>
			</div>
		</div>
		<alert-modal :message="uploadErrorMessage || 'You are exceeding your storage limit. Please upgrade to continue'"
			modal-id="alert-storage-exceeding" ok-text="Got it, thanks" button-alignment="text-right" />
		<alert-modal modal-id="alert-video-max-size"
			:message="maxFileSizeErrorMessage || 'This file is too large. Upload a smaller file or upgrade your plan to continue.'"
			ok-text="Got it, thanks" button-alignment="text-right" />
		<alert-modal modal-id="alert-video-bad-codec"
			message="Only MP4 videos encoded with the H.264 codec are supported. Please ensure your video is in the correct format and encoding before uploading."
			ok-text="Got it, thanks" button-alignment="text-right" @modal-confirm="removeFileFromUpload" />
		<upload-alert-bad-format-modal modal-id="alert-video-bad-format" :message="errorMessage" :body="errorBody"
			@modal-confirm="continueUpload" ok-text="Upload Anyways" button-alignment="text-right" />
	</div>
</template>
<script>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus'
import FormProgress from '@/components/Atoms/FormProgress.vue'
import axios from 'axios'
import StreamService from '@/services/StreamService'
import { mapGetters } from 'vuex'
import AlertModal from '@/components/modals/AlertModal.vue'
import UploadAlertBadFormatModal from '@/components/modals/UploadAlertBadFormatModal.vue';
import UserService from "@/services/UserService"
import FormButton from '@/components/Atoms/FormButton.vue';
import { track, EVENTS } from '@/lib/tracking'
import IconBase from '@/components/icon/IconBase.vue';
import IconChevronDownCompact from '@/components/icon/IconChevronDownCompact.vue';
import IconAlertTriangle from '@/components/icon/IconAlertTriangle.vue';
import IconCloudUpload from '@/components/icon/IconCloudUpload.vue';
import IconCloseThin from '@/components/icon/IconCloseThin.vue';
import IconCheckmark from '@/components/icon/IconCheckmark.vue';

export default {
	name: 'UploadFiles',
	components: {
		FormProgress,
		FormButton,
		AlertModal,
		UploadAlertBadFormatModal,
		IconBase,
		IconChevronDownCompact,
		IconAlertTriangle,
		IconCloudUpload,
		IconCloseThin,
		IconCheckmark,
	},
	props: {

	},
	data() {
		return {
			availableEncodingPresets: [
				{ id: '360p', text: '360p', minHeight: 300 },
				{ id: '480p', text: '480p', minHeight: 440 },
				{ id: '720p', text: '720p', minHeight: 700 },
				{ id: '1080p', text: '1080p', minHeight: 1020 }
			],
			uploadErrorMessage: null,
			maxFileSizeErrorMessage: null,
			maxFileNumberErrorMessage: null,
			errorMessage: null,
			errorBody: null,
			canUpload: true,
			tempoFile: null,
			setupOpen: true,
		}
	},
	computed: {
		...mapGetters({
			loadingStatus: "Ui/loadingStatus",
			uploadFiles: "Ui/uploadFiles",
			streamToUpload: "Ui/streamToUpload",
			canEncodeVideos: "User/canEncodeVideos",
			vodStreams: 'Streams/vodStreams',
		}),
		shouldCheckMediaQuality() {
			return this.$route.name === 'ScheduledStreamManageDashboard'
		},
		// Count only files that are still uploading/queued — exclude ones that
		// have finished (progress hit 100% or returned an upload result).
		uploadingCount() {
			return this.uploadFiles.filter(
				(f) => !(f.uploadResult || (typeof f.uploadProgress === 'number' && f.uploadProgress >= 100))
			).length;
		},
	},
	watch: {
		uploadFiles: {
			// `mediaInfo` is parsed asynchronously and attached to each file after
			// it enters the store. Because this is a deep watcher it re-fires when
			// that happens, so validation is driven off `mediaInfo` becoming
			// available rather than a fixed timer (the old 500ms setTimeout fired
			// before parsing finished on large files, falsely flagging valid H.264
			// videos as "bad codec").
			handler(newValue) {
				// Forget files that have left the list so re-added ones re-validate.
				const activeKeys = new Set(newValue.map(file => this.uploadKeyFor(file)))
				this.processedFiles.forEach(key => {
					if (!activeKeys.has(key)) this.processedFiles.delete(key)
				})
				newValue.forEach(file => this.validateUploadFile(file))
			},
			deep: true
		},
		canUpload() {
			if (this.canUpload) {
				this.uploadFiles.map(file => {
					if (file.uploadable && !file.uploading) {
						this.uploadVideoFile(file)
					}
				})
			}
		}
	},
	created() {
		// Non-reactive set tracking files whose upload is currently in flight or
		// queued, so the deep watcher cannot start a second upload for the same
		// file. Combined with the sequential queue below this prevents both
		// duplicate uploads and parallel-completion races.
		this.inflightUploads = new Set();
		this.uploadQueue = [];
		this.isProcessingQueue = false;

		// Non-reactive set of files whose media has already been validated, keyed
		// by uploadKey. Media parsing is async, so the deep watcher may fire many
		// times per file (parse completion, progress ticks); this ensures each
		// file is validated — and its upload/modal decision made — exactly once.
		// Pruned in the watcher when a file leaves the list, so re-added files
		// (e.g. after removing a bad-codec file) get re-validated.
		this.processedFiles = new Set();
	},
	async mounted() {
		this.$store.dispatch('Ui/dropFilesToUpload');
		this.$store.dispatch('Ui/dropStreamToUpload');
	},
	beforeUnmount() {
		this.setupAWSLibs();
		this.$store.dispatch('Ui/dropFilesToUpload');
		this.$store.dispatch('Ui/dropStreamToUpload');
	},
	methods: {
		validateUploadFile(file) {
			if (file.durationExceeded) return

			// `mediaInfo` is populated asynchronously after the file enters the
			// store. Skip until it exists — the deep watcher re-fires once it's set.
			if (!file.mediaInfo) return

			// Decide exactly once per file. Without this, in-place mutations such as
			// upload-progress ticks would re-run the check and re-emit the modal /
			// re-trigger the upload on every tick.
			const key = this.uploadKeyFor(file)
			if (this.processedFiles.has(key)) return
			this.processedFiles.add(key)

			const isAllowedCodec = !!file.mediaInfo.codecs?.find(element => element.codec === 'AVC')
			if (!isAllowedCodec) {
				this.canUpload = false
				this.tempoFile = file
				showModal('alert-video-bad-codec')
				return
			}

			if (file.mediaInfo.isVbr) {
				this.canUpload = false
				showModal('alert-video-bad-format')
				return
			}

			if (file.uploadable && !file.uploading) {
				this.uploadVideoFile(file)
			}
		},
		isMediaCompatible(mediaInfo) {
			if (!this.shouldCheckMediaQuality) return true

			if (mediaInfo.hasAudioTrack && !mediaInfo.isVbr) return true
			return false
		},
		uploadKeyFor(uploadItem) {
			return uploadItem.id || `${uploadItem.fileName}::${uploadItem.bytes}`;
		},
		async uploadVideoFile(uploadItem) {
			const uploadKey = this.uploadKeyFor(uploadItem);
			// Guard against the deep `uploadFiles` watcher (and the canUpload
			// watcher) invoking this multiple times for the same file before the
			// async `uploading` flag is persisted. That race previously caused
			// duplicate uploads, especially when selecting multiple files at once.
			if (this.inflightUploads.has(uploadKey)) return;
			if (uploadItem.uploading) return;
			this.inflightUploads.add(uploadKey);

			// TV Playout v2 uploads run strictly one-at-a-time. Parallel uploads
			// caused a race where the first file to reach 100% triggered
			// `dropFilesToUpload`, clearing the list while sibling uploads were
			// still finishing (lost last file + "index -1" store errors).
			if (this.streamToUpload && this.streamToUpload._playoutDir) {
				this.uploadQueue.push(uploadItem);
				this.processUploadQueue();
				return;
			}

			try {
				await this.performUploadVideoFile(uploadItem);
			} finally {
				this.inflightUploads.delete(uploadKey);
			}
		},
		async processUploadQueue() {
			if (this.isProcessingQueue) return;
			this.isProcessingQueue = true;
			try {
				while (this.uploadQueue.length) {
					const item = this.uploadQueue.shift();
					const key = this.uploadKeyFor(item);
					try {
						await this.performUploadVideoFile(item);
					} finally {
						this.inflightUploads.delete(key);
					}
				}
			} finally {
				this.isProcessingQueue = false;
			}
		},
		async performUploadVideoFile(uploadItem) {
			if (this.streamToUpload.type === 'vod' && this.canEncodeVideos) {
				// auto video encoding if has special subscription plan
				const maxHeight = uploadItem.mediaInfo.height
				const encodingPresets = _.map(_.filter(this.availableEncodingPresets, i => maxHeight >= i.minHeight), 'id')
				this.$store.dispatch('Ui/addFileData', { uploadItem, encodingPresets: encodingPresets });
				// console.log('encoding video to=', JSON.stringify(uploadItem.encodingPresets))
			}
			this.$store.dispatch('Ui/addFileData', { uploadItem, statusProcessing: true });
			// uploadItem.statusProcessing = true;
			const isReplacingExistingVod = uploadItem.isReplacingExistingFile;
			let mediaUploadSpec;
			let replaceValidateFailed = false;
			try {
				if (isReplacingExistingVod) {
					const res = await this.validateReplaceStorage(uploadItem);
					if (!res) {
						replaceValidateFailed = true;
						return;
					}
					mediaUploadSpec = await this.requestSignedUploadUrlForReplacingVideo(uploadItem);
				} else {
					mediaUploadSpec = await this.requestSignedUploadUrl(uploadItem);
				}
			} catch (e) {
				console.log(e);
				this.uploadErrorMessage = 'We are unable to upload your file, please try again. If the issue persist then please contact live support.';
				showModal('alert-storage-exceeding');
				return;
			} finally {
				// uploadItem.statusProcessing = false;
				this.$store.dispatch('Ui/dropVideoStatus', uploadItem);
				if (replaceValidateFailed) {
					this.$store.dispatch('Ui/dropStreamToUpload');
					this.$store.dispatch('Ui/dropFilesToUpload');
				}
			}

		const uploadUrl = mediaUploadSpec.uploadUrl;
		this.$store.dispatch('Ui/addFileData', { uploadItem, uploading: true });
		if (!isReplacingExistingVod && this.streamToUpload?.type === 'vod') {
			track(EVENTS.VOD_UPLOAD_STARTED, {
				folderId: this.streamToUpload._id,
				fileName: uploadItem.fileName,
				fileSize: uploadItem.file?.size,
				format: uploadItem.file?.type,
				source: 'computer',
			})
		}
		// uploadItem.uploading = true;
			// const onBytes = uploaded => uploadItem.uploadProgress = uploaded;
			const onBytes = _.throttle(
			uploaded => this.$store.dispatch('Ui/addFileData', { uploadItem, uploaded }),
			500);

			const cancelSource = axios.CancelToken.source();
			// uploadItem.cancelSource = cancelSource;
			this.$store.dispatch('Ui/addFileData', { uploadItem, cancelSource });

			let success = false;
			try {
			if (mediaUploadSpec.uploadType === 'direct') {
				await StreamService.uploadVodMediaToBucket(
					uploadUrl,
					uploadItem.file,
					onBytes,
					cancelSource.token
				);
				success = true;
			} else if (mediaUploadSpec.uploadType === 'multipart-presigned') {
				success = await this.startMultipartPresignedUpload(
					mediaUploadSpec, uploadItem, onBytes, cancelSource
				);
			} else if (mediaUploadSpec.uploadType === 'multipart') {
				success = await this.startMultipartBucketUpload(mediaUploadSpec.uploadId,
					uploadItem, mediaUploadSpec.uploadCredentials, onBytes)
			}

			} catch (e) {
				console.log(e);
				this.$store.dispatch('Ui/addFileData', { uploadItem, uploadProgress: 0, uploadError: true, cancelSource: null, uploading: false })
				// uploadItem.uploadProgress = 0;
				// uploadItem.uploadError = true;
				// uploadItem.cancelSource = null;
				// uploadItem.uploading = false;
				return;
			}

			let uploadResult;
			if (success) {
				try {
					// Mark ad-media uploads in new UI
					const isAdFile = uploadItem.id && String(uploadItem.id).startsWith('unnamed_ad_');
					if (isAdFile || this.$route.name === 'StreamsManageAdvertising' || this.$route.name === 'StreamsManageVodAdvertising') {
						this.$store.dispatch('Ui/addFileData', { uploadItem, advMedia: true })
						// uploadItem.mediaInfo.advMedia = true;
					}
					if (isReplacingExistingVod) {
						uploadResult = await StreamService.onCompleteVodReplacement(
							uploadItem.currFile.id,
							mediaUploadSpec.uploadId,
							this.streamToUpload._id,
							this.streamToUpload.key,
							uploadItem
						);
						// this will clear captions, chapeters, schedule, and posters.
						await StreamService.clearVodMetadata(this.streamToUpload._id, uploadItem.currFile.id, uploadItem.currFile._id);
					} else {
						uploadResult = await StreamService.updateVodMediaUploadState(
							mediaUploadSpec.uploadId,
							this.streamToUpload._id,
							this.streamToUpload.key,
							uploadItem,
							this.streamToUpload._playoutDir
						);
					}
				} catch (e) {
					this.$store.dispatch('Ui/addFileData', { uploadItem, uploadProgress: 0, uploadError: true })
					// uploadItem.uploadProgress = 0;
					// uploadItem.uploadError = true;
				}
			}
			this.$store.dispatch('Ui/addFileData', { uploadItem, uploading: false })
			// uploadItem.uploading = false;
			if (isReplacingExistingVod) {
				this.onVideoReplaced(this.streamToUpload._id, uploadItem.currFile.id);
			} else if (uploadResult) {
				this.onVideoUploaded(uploadItem, uploadResult);
			}
		},
	async requestSignedUploadUrl(uploadItem) {
		const fileObj = uploadItem.file;
		const isScheduled = this.streamToUpload.type === 'scheduled';
		const isVod = this.streamToUpload.type === 'vod';
		const isPlayout = !!this.streamToUpload._playoutDir;
		const isAdFile = uploadItem.id && String(uploadItem.id).startsWith('unnamed_ad_');
		const useFastlyUpload = (isScheduled || isVod) && !isAdFile;

		const streamKey = isScheduled
			? this.streamToUpload._id
			: (this.streamToUpload.key || this.streamToUpload._id);
		const uploadPayload = {
			encodingPresets: uploadItem.encodingPresets,
			metadata: {
				size: fileObj.size,
				type: fileObj.type,
				originalFilename: fileObj.name,
				precedence: uploadItem.precedence
			},
			mediaInfo: uploadItem.mediaInfo,
			streamType: this.streamToUpload.type,
			multipartSupport: fileObj.size > 25 * 1024 * 1024
		};

		// TV Playout v2 uploads go through dedicated playout fastly endpoints.
		if (isPlayout) {
			return await StreamService.requestFastlyPlayoutUploadUrl(
				this.streamToUpload._id,
				this.streamToUpload._playoutDir,
				uploadPayload
			);
		}

		const uploadUrl = useFastlyUpload
			? await StreamService.requestFastlyVodMediaUploadUrl(streamKey, uploadPayload)
			: await StreamService.requestVodMediaUploadUrl(streamKey, uploadPayload);
		return uploadUrl;
	},
	async requestSignedUploadUrlForReplacingVideo(uploadItem) {
		const fileObj = uploadItem.file;
		const uploadPayload = {
			encodingPresets: uploadItem.encodingPresets,
			metadata: {
				size: fileObj.size,
				type: fileObj.type,
				originalFilename: fileObj.name,
				precedence: uploadItem.precedence
			},
			mediaInfo: uploadItem.mediaInfo,
			streamType: this.streamToUpload.type,
			multipartSupport: fileObj.size > 25 * 1024 * 1024
		};
			const currFile = uploadItem.currFile;
			const uploadUrl = await StreamService.requestVodMediaReplaceUploadUrl(this.streamToUpload.key, currFile.id, uploadPayload);
			return uploadUrl;
		},
		async validateReplaceStorage(uploadItem) {
			const assignedSpace = parseFloat(this.$store.state.User.maxVodStorage);
			const existingFileStorage = uploadItem.currFile.bytes;
			const newFileStorage = uploadItem.bytes;
			let freeSpaceAvailable = 0;

			let totalVodBytes = 0;
			if (assignedSpace) {
				const streams = this.vodStreams;

				streams.forEach((item) => {
					if (item.type === 'vod' && !item.liveParent) {
						totalVodBytes += item.totalBytes
					}
				})

				if (totalVodBytes > 0) {
					totalVodBytes = totalVodBytes / (1000 ** 3)
				}

				freeSpaceAvailable = (assignedSpace - totalVodBytes) * (1000 ** 3);
			}
			if (freeSpaceAvailable + existingFileStorage - newFileStorage < 0) {
				showModal('max-size-vod-storage-reached');
				return false;
			}
			return true;
		},
	async onVideoReplaced(streamId, videoId) {
		await this.$store.dispatch('Streams/getStreams');
		await this.$store.dispatch('Streams/getFilesInVODFolder')
		this.$notify({
			group: 'success',
			text: 'Video replaced successfully.'
		});
		this.showOptions = false;
		this.$store.dispatch('Ui/dropStreamToUpload')
		this.$store.dispatch('Ui/dropFilesToUpload')
		// Emit global event to notify other components
		emitAppEvent(APP_EVENTS.VIDEO_UPLOADED);
	},
		async onVideoUploaded(uploadItem, uploadResult) {
			if (!uploadResult || !uploadResult.fileId) return;
			this.$store.dispatch('Ui/addFileData', { uploadItem, uploadResult })
			const allUploaded = this.uploadFiles.every(item => item.uploadProgress === 100)

			// TV Playout v2 uploads: skip the VOD-specific refresh/metadata calls
			// (which rely on route.params.streamId and VOD folder state). Just notify
			// listeners so the playout directory reloads with the new file.
			if (this.streamToUpload?._playoutDir) {
				this.$notify({ group: 'success', text: 'Video uploaded successfully.' });
				this.$emit('video-added', uploadItem);
				emitAppEvent(APP_EVENTS.VIDEO_UPLOADED, uploadItem);
				this.showOptions = false;
				if (allUploaded) {
					this.$store.dispatch('Ui/dropStreamToUpload')
					this.$store.dispatch('Ui/dropFilesToUpload')
				}
				return;
			}

			track(EVENTS.VOD_UPLOADED, {
				folderId: this.streamToUpload?._id,
				fileId: uploadResult.fileId,
				fileSize: uploadItem.file?.size,
				source: 'computer',
			})

			await this.$store.dispatch('Streams/getStreams');
			
			try {
				await this.$store.dispatch('Streams/getFilesInVODFolder')
			} catch (e) { /* no folder open — nothing to refresh */ }
			this.$notify({
				group: 'success',
				text: 'Video uploaded successfully.'
			});
			this.$emit('video-added', uploadItem);
			// Emit global event to notify other components (e.g., ScheduleStreamPreviewPane)
			emitAppEvent(APP_EVENTS.VIDEO_UPLOADED, uploadItem);
			setTimeout(() => {
				this.$notify({ group: 'info', text: 'Video added to media list' });
			}, 2000);
			setTimeout(async () => {
				// if(this.$store?.state?.User?.activeTeam) {
				await StreamService.updateStreamPlaylistVideoMetaData(this.$route.params.streamId, uploadItem.id, {
					author: this.$store?.state?.User?.activeTeam ? this.$store?.state?.User?.teamMemberUser?.email : this.$store?.state?.User?.email,
				});
				// }
			})
		this.showOptions = false;
		if (allUploaded) {
			this.$store.dispatch('Ui/dropStreamToUpload')
			this.$store.dispatch('Ui/dropFilesToUpload')
		}
	},
	async startMultipartPresignedUpload(mediaUploadSpec, uploadItem, onBytesUploaded, cancelSource) {
		const file = uploadItem.file;
		const { parts, chunkSize, multipartUploadId, uploadId } = mediaUploadSpec;
		const streamKey = this.streamToUpload.type === 'scheduled'
			? this.streamToUpload._id
			: (this.streamToUpload.key || this.streamToUpload._id);
		const concurrency = file.size > 1024 * 1024 * 1024 ? 2 : 4;
		let totalUploaded = 0;
		let cancelled = false;
		const completedParts = [];

		cancelSource.cancel = () => { cancelled = true; };

		const partQueue = [...parts];

		const uploadNextPart = async () => {
			while (partQueue.length > 0 && !cancelled) {
				const part = partQueue.shift();
				const start = (part.partNumber - 1) * chunkSize;
				const end = Math.min(start + chunkSize, file.size);
				const blob = file.slice(start, end);

				let attempts = 0;
				let success = false;
				while (attempts < 3 && !success && !cancelled) {
					try {
					const response = await fetch(part.url, {
						method: 'PUT',
						headers: { 'Content-Type': file.type },
						body: blob,
					});
					if (!response.ok) throw new Error('Part failed: ' + response.status);
					const etag = response.headers.get('etag') || '';
						completedParts.push({
							partNumber: part.partNumber,
							etag: etag.replace(/"/g, '')
						});
						success = true;
					} catch (e) {
						attempts++;
						if (attempts >= 3) throw e;
						await new Promise(r => setTimeout(r, attempts * 2000));
					}
				}

				totalUploaded += (end - start);
				onBytesUploaded(Math.min((totalUploaded / file.size) * 100, 99.9));
			}
		};

		try {
			const workers = [];
			for (let i = 0; i < Math.min(concurrency, parts.length); i++) {
				workers.push(uploadNextPart());
			}
			await Promise.all(workers);

			if (cancelled) return false;

			completedParts.sort((a, b) => a.partNumber - b.partNumber);

			if (this.streamToUpload._playoutDir) {
				await StreamService.completeFastlyPlayoutMultipartUpload(
					this.streamToUpload._id, this.streamToUpload._playoutDir,
					uploadId, multipartUploadId, completedParts, this.streamToUpload.type
				);
			} else {
				await StreamService.completeFastlyMultipartUpload(
					streamKey, uploadId, multipartUploadId,
					completedParts, this.streamToUpload.type
				);
			}

			onBytesUploaded(100);
			return true;
		} catch (e) {
			console.error('Multipart presigned upload failed:', e);
			try {
				if (this.streamToUpload._playoutDir) {
					await StreamService.abortFastlyPlayoutMultipartUpload(
						this.streamToUpload._id, this.streamToUpload._playoutDir,
						uploadId, multipartUploadId, this.streamToUpload.type
					);
				} else {
					await StreamService.abortFastlyMultipartUpload(
						streamKey, uploadId, multipartUploadId, this.streamToUpload.type
					);
				}
			} catch (err) { }
			return false;
		}
	},
	startMultipartBucketUpload(uploadId, uploadItem, uploadCredentials, onBytesUploaded) {
			return new Promise((resolve) => {
				let AWS = window.AWS
				AWS.config.update({
					region: uploadCredentials.region,
					useAccelerateEndpoint: uploadCredentials.useAccelerateEndpoint
				})

				// Initialize the Amazon Cognito credentials provider
				let identityConf = {
					IdentityPoolId: uploadCredentials.poolId,
					LoginId: UserService.getUser(this.activeTeam).email
				}

				AWS.config.credentials = new AWS.CognitoIdentityCredentials(identityConf)

				// Upload the File
				const uploadBucket = new AWS.S3({ params: { Bucket: uploadCredentials.bucketName } })
				const uploadOpts = {
					Key: uploadCredentials.keyPrefix + uploadId,
					ContentType: uploadItem.file.type,
					Body: uploadItem.file,
					queueSize: uploadCredentials.concurrency || 2, // default 2 parts in parallel
					partSize: uploadCredentials.chunkSize || (2 * 1000 * 1000), // default 2MB
					UseAccelerateEndpoint: true
				}

				const onBytes = (ev) => {
					let percent = (ev.loaded * 100) / ev.total
					onBytesUploaded(percent)
				}

				let multipartUploadHandle = uploadBucket.upload(uploadOpts)
				multipartUploadHandle.on('httpUploadProgress', onBytes)

				const multipartCancelSource = {
					cancel: () => void multipartUploadHandle.abort()
				}
				this.$store.dispatch('Ui/addFileData', { uploadItem, cancelSource: multipartCancelSource })

				multipartUploadHandle.send((err, data) => {
					if (err) {
						this.$store.dispatch('Ui/addFileData', {
							uploadItem,
							uploadProgress: 0,
							uploadError: true,
							cancelSource: null,
							uploading: false
						})
					}

					resolve(!err)
				})
			})
		},
		cancelVideoUpload(uploadItem) {
			if (!uploadItem) return;

			if (uploadItem.cancelSource) {
				uploadItem.cancelSource.cancel();
			}
			this.$store.dispatch('Ui/cancelVideoUpload', uploadItem);
			// media.uploading = false;
		},
		removeVideoFile(uploadItem) {
			this.$store.dispatch('Ui/removeFileFromUpload', uploadItem)
		},
		removeAll() {
			this.$store.dispatch('Ui/dropFilesToUpload');
		},
		continueUpload() {
			this.canUpload = true
			console.log('continue');
		},
		getErrorMessage(mediaInfo) {
			let message = ''

			if (mediaInfo.isVbr) {
				message = 'File should be CBR-encoded'
			}

			if (!mediaInfo.hasAudioTrack) {
				if (mediaInfo.isVbr) message += ' and have sound'
				else message = 'File should have sound'
			}

			message += '.'

			return message
		},
		removeFileFromUpload() {
			this.$store.dispatch('Ui/removeFileFromUpload', this.tempoFile);
			this.tempoFile = null
		}
	}
}
</script>

<style scoped>
.upload-widget {
	position: fixed;
	bottom: 0;
	right: 2rem;
	width: 24rem;
	border-radius: 8px 8px 0 0;
	box-shadow: 0px 32px 40px 0px #0000001F;
	box-shadow: 0px 8px 12px 0px #00000014;
	z-index: 99;
}

:deep(.upload-widget .btn-sidebar-collapse) {
	border-radius: 8px 8px 0 0;
}

.progressbar-wrapper {
	height: 3px;
	border-radius: 3px;
	background-color: var(--c-neutral-n3);
}

.progress-label {
	position: absolute;
	top: 0;
	right: 1.75rem;
	color: var(--c-dark-6);
}

@media (max-width: 767px) {
	.upload-widget {
		right: auto;
		width: calc(100% - 30px);
		margin: 0 15px;
	}
}

@media (min-width: 992px) {
	.upload-widget {
		right: 6rem;
	}
}
</style>